import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {toTransportCategory, TransportCategory} from "./transport-category";
import {SettlementStatus, toSettlementStatus} from "./settlement-status";
import {ReportTimeType, toReportTimeType} from "./report-time-type";

export const SettlementValidator = t.intersection([
  t.type({
    id: t.string,
    activityPeriodId: t.string,
    status: t.string,
    regionId: t.string,
  }),
  t.partial({
    name: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,

    activityPeriodCompanyId: t.string,
    activityPeriodCompanyName: t.string,
    activityPeriodCompanyUnitId: t.string,
    activityPeriodCompanyUnitName: t.string,
    activityPeriodFromDttm: t.number,
    activityPeriodToDttm: t.number,
    activityPeriodType: t.string,
    activityPeriodCategory: t.string,
  }),
]);

export const SettlementArrayValidator = t.array(SettlementValidator);

export type Settlement = t.TypeOf<typeof SettlementValidator>;
export type SettlementArray = t.TypeOf<typeof SettlementArrayValidator>;

@Exclude()
export class SettlementEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  name?: string;

  @Expose()
  activityPeriodId!: string;

  @Expose()
  activityPeriodCompanyId?: string

  @Expose()
  activityPeriodCompanyName?: string

  @Expose()
  activityPeriodCompanyUnitId?: string

  @Expose()
  activityPeriodCompanyUnitName?: string

  @Expose()
  activityPeriodFromDttm?: number

  @Expose()
  activityPeriodToDttm?: number

  @Transform(getDatabaseCodeTransformer(toReportTimeType))
  @Expose()
  activityPeriodType?: ReportTimeType;

  @Transform(getDatabaseCodeTransformer(toTransportCategory))
  @Expose()
  activityPeriodCategory?: TransportCategory;

  @Transform(getDatabaseCodeTransformer(toSettlementStatus))
  @Expose()
  status!: SettlementStatus;

  @Expose()
  regionId!: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'SETTLEMENT';

  get displayName(): Readonly<string> {
    return this.name ?? this.id;
  }

  /**
   * Constructor for SettlementEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: Settlement): SettlementEntity {
    return plainToInstance(SettlementEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<Settlement> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(SettlementValidator, object);
  }

  asTypeValue(): Settlement {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(SettlementValidator, object);
  }
}

export type SettlementSearchCriteria = {
  fromDttm?: number;
  toDttm?: number;
  regionIds?: string[];
  operatorIds?: string[];
  truckCompanyIds?: string[];
  categories?: string[];
  types?: string[]
  statuses?: string[]
};
